import React, { useState, useEffect } from 'react';
import isFunction from 'lodash/isFunction';
import shortid from 'shortid';
import useForm from '../hooks/useForm';
import Adapter from './Adapter';
import getFieldComponent from '../utils/getFieldComponent';

function mapActionKeys(actions) {
  if (!Array.isArray(actions)) return actions;

  return actions.map((action) => {
    if (!('key' in action)) {
      return {
        ...action,
        key: shortid.generate(),
      };
    }

    return action;
  });
}

const defaultActions = [
  {
    label: 'Submit',
    primary: true,
  },
];

export default function Form({ actions = defaultActions, children, title, description, ...props }) {
  const {
    adapter,
    defaultHandler,
    formApi,
    formContext,
    formState,
    formProps,
    render,
    userProps,
  } = useForm(props);

  const [formActions, setFormActions] = useState(() => mapActionKeys(actions));

  useEffect(() => {
    setFormActions(mapActionKeys(actions));
  }, [actions]);

  const { $submitting, compiledSchema } = formContext;

  if (isFunction(children)) {
    return render(
      <Adapter
        component="Form"
        {...formProps}
        context={formContext}
        defaultHandler={defaultHandler}
        formApi={formApi}
        formState={formState}
        userProps={userProps}
      >
        {children({ ...formContext, formApi, formState })}
      </Adapter>
    );
  }

  const FieldComponent = getFieldComponent(adapter, compiledSchema);

  return render(
    <Adapter
      component="Form"
      {...formProps}
      context={formContext}
      defaultHandler={defaultHandler}
      formApi={formApi}
      formState={formState}
      userProps={userProps}
    >
      {children == null ? (
        <React.Fragment>
          <FieldComponent $ref="" title={title} description={description} />
          {Array.isArray(formActions) &&
            formActions.map(
              ({
                component: ActionComponent = React.createElement(Adapter, { component: 'Button' }),
                transformProperties,
                ...formActionProps
              }) => {
                const actionProps = {
                  disabled: $submitting,
                  ...(isFunction(transformProperties)
                    ? transformProperties(formActionProps, formContext)
                    : formActionProps),
                };

                if (React.isValidElement(ActionComponent)) {
                  return React.cloneElement(ActionComponent, actionProps);
                }

                return <ActionComponent {...actionProps} />;
              }
            )}
        </React.Fragment>
      ) : (
        children
      )}
    </Adapter>
  );
}
